import { calcPercentage, SegmentTotalsRecord, SeriesData } from '@/components/Survey/Utilities/ChartUtilities'

/**
 * Sort single select or multi select index data from greatest to least while respecting the locked questions
 * @param seriesData - The series data that is being sorted
 * @param lockedSeriesData - The locked options that will be added in order after sorting
 * @param beginningOptions - Options that will always be first (currently Yes and No, in that order)
 * @param endOptions - The options that will always be at the end (currently Other or "None of the above")
 */
export const sortSelectQuestionSeries = (
  seriesData: SeriesData[],
  lockedSeriesData: { order: number, data: SeriesData }[],
  beginningOptions: SeriesData[],
  endOptions: SeriesData[]
) => {
  let sortedSeriesData: SeriesData[] = []
  if (beginningOptions.length === 2) {
    // Put yes before no
    beginningOptions.sort((a, b) => (a.name > b.name) ? -1 : 1)
    sortedSeriesData = [...beginningOptions]
  } else {
    // Only one of yes or no was there... edge case, put it in sorted
    seriesData = seriesData.concat(beginningOptions)
  }

  // Greatest to least
  seriesData.sort((a, b) => (a.y > b.y) ? -1 : 1)
  // Least to greatest
  lockedSeriesData.sort((a, b) => (a.order < b.order) ? -1 : 1)

  const optionCount = (seriesData.length || sortedSeriesData.length) + lockedSeriesData.length // Exclude end options because we concat it at end
  for (let i = 0; i < optionCount; i++) {
    // Locked series is sorted, so only need to check first element
    if (lockedSeriesData.length && lockedSeriesData[0].order === i) {
      const data = lockedSeriesData.shift()!.data
      sortedSeriesData.push(data)
    } else if (seriesData.length) {
      const data = seriesData.shift()!
      sortedSeriesData.push(data)
    }
  }

  // Add ending options
  return sortedSeriesData.concat(endOptions)
}

/***
 * Sort rank or matrix question index data by the order specified in the reverse property. If the first ranking totals are
 * equal, the next value in ranking scale will be used to sort.
 * @param rankings - The ranking totals that are being sorted
 * @param sortKey - The ranking being used to sort by ... matrix uses high end of it's ranking scale, rank uses low end
 * @param reverse - Controls sorting order. Default is G to L, reversed is L to G
 */
export const sortRankingObject = (rankings: SegmentTotalsRecord, sortKey: number, reverse: boolean): SegmentTotalsRecord => {
  const lockedRankings: Record<string, { key: string, totals: Record<string, number> }> = {}
  const unlockedRankings: SegmentTotalsRecord = {}
  const sortedRankings: SegmentTotalsRecord = {}

  // Matrix questions have option lock capability, this will be ignored for rank
  Object.entries(rankings).forEach(([key, totals]) => {
    if (totals.order !== undefined) {
      lockedRankings[totals.order] = { key, totals }
    } else {
      unlockedRankings[key] = totals
    }
  })

  const sortedList = Object.entries(unlockedRankings)
    .sort((a, b) => {
      const aValue = calcPercentage(a[1][sortKey], a[1].segmentCount)
      const bValue = calcPercentage(b[1][sortKey], b[1].segmentCount)

      const secondSortKey = reverse ? sortKey + 1 : sortKey - 1
      const nextAValue = calcPercentage(a[1][secondSortKey], a[1].segmentCount)
      const nextBValue = calcPercentage(b[1][secondSortKey], b[1].segmentCount)

      if (reverse) {
        // Least to greatest
        if ((aValue < bValue)) return 1 // Shift
        else if ((aValue === bValue)) { // If they are equal, check if the next ranking can be used to sort
          if (nextAValue < nextBValue) return 1 // Shift
        }
        return -1 // Don't shift
      } else {
        // Greatest to least
        if (aValue > bValue) return 1
        else if (aValue === bValue) {
          if (nextAValue > nextBValue) return 1
        }
        return -1
      }
    })

  // Put sorted objects back in original Record form as the sort changes it to a list
  for (let i = 0; i < Object.keys(rankings).length; i++) {
    if (lockedRankings[i]) {
      sortedRankings[lockedRankings[i].key] = lockedRankings[i].totals
    } else {
      const data = sortedList.shift()!
      const optionText = data[0]
      sortedRankings[optionText] = data[1]
    }
  }
  return sortedRankings
}

/***
 * Check if the option is one of the options that come last regardless of sorting
 * @param optionName The name of the option to check
 */
export const isEndOption = (optionName: string) => {
  const endOptions = ['Other', 'None of the above']
  return endOptions.includes(optionName)
}
